import showSonoraClassicAttachmentActionsPanel from '../../components/SonoraClassicAttachmentActionsPanel';
import attachFilesForSelectedAction from './attachFilesForSelectedAction';
import createCloudFileFromSonoraSuggestion from './createCloudFileFromSonoraSuggestion';
import getBreadcrumbedFilePath from './getBreadcrumbedFilePath';
import { formatPastRelativeDateTime, formatUserDateTime, OwaDate, owaDate } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilePickerAction, lazyGetDefaultFileProvider } from 'owa-filepicker';
import { getFileProviders } from 'owa-fileprovider-store';
import { lazyInsertLinks } from 'owa-insert-link';
import loc, { format } from 'owa-localize';
import { observableNow } from 'owa-observable-datetime';
import { SubstrateSearchScenario } from 'owa-search-service';
import {
    SonoraFileLastAccessDateLabel,
    SonoraFileLastModifiedDateLabel,
    SonoraFileCreatedDateLabel,
    SonoraVivaRelatedTopicsLabel,
} from '../SonoraItemResults.locstring.json';
import { log3sEntityClickedAction } from '../../dataLogger/log3sInstrumentation';
import {
    SonoraDataProvider,
    SonoraFile,
    SonoraFileSuggestionSourceType,
    SonoraViewState,
} from 'owa-editor-sonora-types';

export const getPrimaryTextForSonoraResultsFileItems = (suggestion: SonoraFile): string => {
    return suggestion.hitFileName || suggestion.fileName + '.' + suggestion.fileExtension;
};

export const getSecondaryTextForSonoraResultsFileItems = (suggestion: SonoraFile): string => {
    if (
        suggestion.sourceType === SonoraFileSuggestionSourceType.ClassicAttachment &&
        suggestion.subject
    ) {
        return suggestion.subject;
    } else if (suggestion.relatedTopicName) {
        return loc(SonoraVivaRelatedTopicsLabel, suggestion.relatedTopicName);
    }

    return getAccessDate(suggestion) || getModifiedDate(suggestion);
};

export const getTooltipStringForSonoraResultsFileItems = (suggestion: SonoraFile): string => {
    let tooltipString: string;
    if (suggestion.sourceType === SonoraFileSuggestionSourceType.ClassicAttachment) {
        tooltipString = [suggestion.fileName, suggestion.subject, getCreatedDate(suggestion)]
            .filter(str => str)
            .join('\n');
    } else {
        tooltipString = [
            suggestion.fileName,
            getBreadcrumbedFilePath(suggestion),
            getAccessDate(suggestion),
            getModifiedDate(suggestion),
        ]
            .filter(str => str)
            .join('\n');
    }
    return tooltipString;
};

export const insertFileSuggestionsIntoComposeEditor = async (
    suggestion: SonoraFile,
    insertNodeCallback: (nodeToInsert: HTMLElement) => void,
    dataProvider: SonoraDataProvider
) => {
    const {
        adapter,
        editorAdapter,
        attachmentWellAdapter,
        insertLinksHost,
        fromAddress,
        createAttachmentsCallback,
    } = dataProvider;

    const viewState = adapter();

    const logicalId = viewState.results.fileSuggestions.logicalId ?? '';
    const traceId = viewState.results.fileSuggestions.traceId ?? '';
    log3sEntityClickedAction(logicalId, traceId, SubstrateSearchScenario.SonoraFilePicker);

    if (suggestion.sourceType === SonoraFileSuggestionSourceType.ClassicAttachment) {
        const br = document.createElement('br');
        insertNodeCallback(br);

        if (isFeatureEnabled('honeybee-sonora-mailboxFileUpload')) {
            const getDefaultFileProvider = await lazyGetDefaultFileProvider.import();
            const fileProvidersViewState = getFileProviders();
            const defaultFileProvider = getDefaultFileProvider(fileProvidersViewState);

            if (defaultFileProvider) {
                // If the feature flag is on and there is a default file provider, show the action panel.
                const actionsPanel = (viewState: SonoraViewState) =>
                    new Promise<FilePickerAction>((resolve, reject) => {
                        // Todo: Add more actions when supported.
                        const supportedActions = [FilePickerAction.Share, FilePickerAction.Attach];
                        showSonoraClassicAttachmentActionsPanel(
                            resolve,
                            reject,
                            viewState,
                            supportedActions,
                            defaultFileProvider!
                        );
                    });
                actionsPanel(viewState).then(action =>
                    attachFilesForSelectedAction(
                        suggestion,
                        action,
                        attachmentWellAdapter(),
                        createAttachmentsCallback
                    )
                );
                return;
            }
        }

        // Otherwise, attach as a copy.
        attachFilesForSelectedAction(
            suggestion,
            FilePickerAction.Attach,
            attachmentWellAdapter(),
            createAttachmentsCallback
        );
    } else {
        const span = document.createElement('span');
        insertNodeCallback(span);

        const cloudFile = createCloudFileFromSonoraSuggestion(suggestion);

        // Insert a beautified link into compose pane directly
        const insertLinks = await lazyInsertLinks.import();
        insertLinks(
            [cloudFile] /* AttachmentFileList */,
            insertLinksHost /* InsertLinksHost */,
            editorAdapter() /* EditorViewState */,
            fromAddress /* fromAddress */,
            window /* targetWindow */
        );
    }
};

const getAccessDate = (suggestion: SonoraFile): string => {
    const { lastAccessDateTime } = suggestion;
    if (!lastAccessDateTime) {
        return '';
    }
    const date: OwaDate = owaDate('UTC', lastAccessDateTime);
    const relativeDateText = formatPastRelativeDateTime(date, observableNow());
    return format(loc(SonoraFileLastAccessDateLabel), relativeDateText);
};

const getModifiedDate = (suggestion: SonoraFile): string => {
    const { lastModifiedDateTime } = suggestion;
    if (!lastModifiedDateTime) {
        return '';
    }
    const date: OwaDate = owaDate('UTC', lastModifiedDateTime);
    const dateText: string = formatUserDateTime(date);
    return format(loc(SonoraFileLastModifiedDateLabel), dateText);
};

const getCreatedDate = (suggestion: SonoraFile): string => {
    const { createdDateTime } = suggestion;
    if (!createdDateTime) {
        return '';
    }
    const date: OwaDate = owaDate('UTC', createdDateTime);
    const dateText: string = formatUserDateTime(date);
    return format(loc(SonoraFileCreatedDateLabel), dateText);
};
